import { useState, useEffect, useRef } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { isFuture, isPast, format, startOfMonth, endOfMonth, addMonths, startOfYear, endOfYear } from "date-fns"
import { isInscricaoEncerrada } from "@/utils/date"
import { useNavigate } from "react-router-dom"

// Services & Types
import {
  deleteTechnicalFile,
  getCalendarioAction,
} from "@/modules/Events/service/EventsService"
import { CalendarioMeta, EventoDataType, GetCalendarioParamsDataType } from "@/types/events"

// Hooks
import { useLoading } from "@/hooks/useLoading"
import { useAuth } from "@/modules/Auth/context/AuthContext"

// Components
import { EventCardV2 } from "@/modules/Events/components/EventCardV2"
import { BackofficeEventCard } from "@/modules/Events/components/BackofficeEventCard"
import { BatchSubscribeModal } from "@/modules/Events/components/BatchSubscribeModal"
import { MemberModal } from "@/modules/Users/components/MemberModal"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import { PDFModal } from "../components/PDFModal"
import { EventsFilter } from "@/modules/Events/components/EventsFilter"
import { useSubscribeConfirm } from "@/modules/Events/context/SubscribeConfirmContext"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { toast } from "sonner"

// --- Componentes Auxiliares Locais ---

const CustomModal = ({
  isOpen,
  title,
  description,
  onClose,
}: {
  isOpen: boolean
  title: string
  description: string
  onClose: () => void
}) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => onClose(), 3000)
      return () => clearTimeout(timer)
    }
  }, [isOpen, onClose])

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose}>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

const GlassSubtitle = ({
  children,
  icon,
  className = "",
}: {
  children: React.ReactNode
  icon?: string
  className?: string
}) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    className={`inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-1.5 shadow-xl backdrop-blur-md ${className}`}
  >
    {icon && (
      <i className={`fi fi-ts-${icon} flex items-center justify-center text-sm text-primary`} />
    )}
    <span className="text-sm font-semibold uppercase tracking-wider text-zinc-200">{children}</span>
  </motion.div>
)

// Evento fixado no topo do carrossel "Próximos Eventos"
const PINNED_EVENT_ID = 172
const PAGE_LIMIT = 10

// Converte o filtro de período da UI para query params da API V2
const buildPeriodParams = (
  periodFilter: string,
  startDate: Date | null,
  endDate: Date | null,
): Pick<GetCalendarioParamsDataType, 'periodo' | 'retroativo' | 'data_ini' | 'data_fim'> => {
  const today = new Date()
  switch (periodFilter) {
    case 'este-mes':
      return {
        data_ini: format(startOfMonth(today), 'yyyy-MM-dd'),
        data_fim: format(endOfMonth(today), 'yyyy-MM-dd'),
      }
    case 'proximo-mes': {
      const next = addMonths(today, 1)
      return {
        data_ini: format(startOfMonth(next), 'yyyy-MM-dd'),
        data_fim: format(endOfMonth(next), 'yyyy-MM-dd'),
      }
    }
    case 'proximos-3-meses':
      return { periodo: 3, retroativo: 'false' }
    case 'este-ano':
      return {
        data_ini: format(startOfYear(today), 'yyyy-MM-dd'),
        data_fim: format(endOfYear(today), 'yyyy-MM-dd'),
      }
    case 'customizado':
      if (startDate && endDate) {
        return {
          data_ini: format(startDate, 'yyyy-MM-dd'),
          data_fim: format(endDate, 'yyyy-MM-dd'),
        }
      }
      return {}
    default:
      return {}
  }
}

// --- Componente Principal ---

export default function BackofficeEventos() {
  const { user } = useAuth()
  const { showLoading, hideLoading } = useLoading()
  const navigate = useNavigate()

  // States
  const [eventos, setEventos] = useState<EventoDataType[]>([])
  const [filteredEventos, setFilteredEventos] = useState<EventoDataType[]>([])
  const [upcomingEvents, setUpcomingEvents] = useState<EventoDataType[]>([])
  const [meta, setMeta] = useState<CalendarioMeta | null>(null)
  const [search, setSearch] = useState("")
  const [inscriptionFilter, setInscriptionFilter] = useState("todos")
  const [periodFilter, setPeriodFilter] = useState("todos")
  const [typeFilter, setTypeFilter] = useState("todos")
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedEventToBatch, setSelectedEventToBatch] = useState<EventoDataType | null>(null)
  const [pdfConfig, setPdfConfig] = useState<{ url: string; title: string } | null>(null)
  const [batchSubscribeModal, setBatchSubscribeModal] = useState(false)
  const [isMemberModalOpened, setIsMemberModalOpened] = useState(false)
  const [memebrId, setMemebrId] = useState<string | number>()
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    description: "",
  })
  const { requestSubscribe } = useSubscribeConfirm()

  // Período, origem e paginação são enviados como query params para a API
  const fetchEventos = (page = currentPage) => {
    const periodParams = buildPeriodParams(periodFilter, startDate, endDate)
    const origemParam = typeFilter !== 'todos' ? { origem: typeFilter as 'evento' | 'imersao' } : {}
    getCalendarioAction({ emp_id: user.empId, page, limit: PAGE_LIMIT, ...periodParams, ...origemParam })
      .then(({ data }) => {
        setEventos(data.data)
        if (data.meta) setMeta(data.meta)
      })
      .catch((err) => console.error(err))
      .finally(() => hideLoading())
  }

  // Refaz o fetch quando período, origem ou página mudam (todos server-side)
  useEffect(() => {
    if (!user) return
    if (periodFilter === 'customizado' && (!startDate || !endDate)) return
    showLoading()
    fetchEventos(currentPage)
  }, [user, periodFilter, startDate, endDate, typeFilter, currentPage])

  useEffect(() => {
    const future = eventos
      .filter(
        (ev) =>
          isFuture(new Date(ev.data_inicio)) &&
          !isInscricaoEncerrada(ev.data_limite)
      )
      .sort((a, b) => {
        if (a.id === PINNED_EVENT_ID && b.id !== PINNED_EVENT_ID) return -1
        if (b.id === PINNED_EVENT_ID && a.id !== PINNED_EVENT_ID) return 1
        return new Date(a.data_inicio).getTime() - new Date(b.data_inicio).getTime()
      })
      .slice(0, 10)
    setUpcomingEvents(future)
  }, [eventos])

  // Filtros client-side: busca e status de inscrição (sem equivalente na API)
  useEffect(() => {
    const filtered = eventos
      .filter((ev) => !search || ev.descricao.toLowerCase().includes(search.toLowerCase()))
      .filter((ev) => {
        if (inscriptionFilter === "inscrito") return ev.inscrito
        if (inscriptionFilter === "disponivel")
          return (
            !ev.inscrito &&
            isFuture(new Date(ev.data_inicio)) &&
            !isInscricaoEncerrada(ev.data_limite)
          )
        if (inscriptionFilter === "encerrados") return isPast(new Date(ev.data_inicio))
        return true
      })
      .sort((a, b) => {
        if (a.id === PINNED_EVENT_ID && b.id !== PINNED_EVENT_ID) return -1
        if (b.id === PINNED_EVENT_ID && a.id !== PINNED_EVENT_ID) return 1
        return 0
      })

    setFilteredEventos(filtered)
  }, [eventos, search, inscriptionFilter])

  // Quando filtros client-side mudam, volta para a primeira página
  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1)
  }, [search, inscriptionFilter])

  const totalPages = meta?.totalPages ?? 1
  const totalCount = meta?.total ?? filteredEventos.length

  const onSubscribeEvent = (evento: EventoDataType) => {
    requestSubscribe(evento, () => fetchEventos(currentPage))
  }

  const handleScroll = (direction: "left" | "right") => {
    if (carouselRef.current) {
      const scrollAmount = direction === "left" ? -900 : 900
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" })
    }
  }

  const handleRemoveFile = async (evento: EventoDataType) => {
    showLoading()
    try {
      await deleteTechnicalFile(evento.id, evento.origem)
      toast.success("Ficha técnica removida com sucesso!")
      fetchEventos(currentPage)
    } catch (error) {
      toast.error("Erro ao remover ficha técnica.")
      console.error("Erro ao remover", error)
    } finally {
      hideLoading()
    }
  }

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return
    setCurrentPage(page)
  }

  const renderPageNumbers = () => {
    if (totalPages <= 1) return null

    const pages: number[] = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    const end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)

    for (let i = start; i <= end; i++) pages.push(i)

    return pages.map((page) => (
      <button
        key={page}
        onClick={() => handlePageChange(page)}
        className={`flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-sm transition-colors ${
          currentPage === page
            ? "bg-orange-500 font-semibold text-white"
            : "border border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
        }`}
      >
        {page}
      </button>
    ))
  }

  return (
    <div className="overflow-hidden">
      <PageHeader
        title="Eventos"
        icon="calendar"
      />

      {upcomingEvents.length > 0 && (
        <div className="mx-6 overflow-hidden rounded-xl border border-orange-500/40 bg-gradient-to-br from-orange-500/20 via-orange-500/5 to-transparent p-4">
          <div className="flex items-center justify-between">
            <GlassSubtitle icon="calendar">Próximos Eventos</GlassSubtitle>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleScroll("left")}
                className="flex size-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:bg-white/10 active:bg-white/20"
              >
                <i className="fi fi-ts-arrow-left flex items-center justify-center" />
              </button>
              <button
                onClick={() => handleScroll("right")}
                className="flex size-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:bg-white/10 active:bg-white/20"
              >
                <i className="fi fi-ts-arrow-right flex items-center justify-center" />
              </button>
            </div>
          </div>
          <div
            ref={carouselRef}
            className="flex w-full gap-4 overflow-x-auto py-4"
          >
            <AnimatePresence>
              {upcomingEvents.map((evento, idx) => (
                <motion.div
                  key={evento.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: idx * 0.2 } }}
                  viewport={{ once: true }}
                >
                  <BackofficeEventCard
                    evento={evento}
                    onCardClick={() => navigate(`/events/${evento.id}`, { state: { evento } })}
                    onSubscribeEvent={() => onSubscribeEvent(evento)}
                    onBatchSubscribe={() => {
                      setSelectedEventToBatch(evento)
                      setBatchSubscribeModal(true)
                    }}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* LISTAGEM */}
      <div className="mt-8 space-y-4 overflow-hidden px-6">
        <div className="flex items-center justify-between text-white">
          <GlassSubtitle icon="calendar">Listagem de eventos</GlassSubtitle>
          <span className="text-sm text-muted-foreground">
            Encontrados: {totalCount}
          </span>
        </div>
        <EventsFilter
          search={search}
          onSearchChange={setSearch}
          inscriptionFilter={inscriptionFilter}
          onInscriptionFilterChange={setInscriptionFilter}
          typeFilter={typeFilter}
          onTypeFilterChange={(v) => {
            setTypeFilter(v)
            setCurrentPage(1)
          }}
          periodFilter={periodFilter}
          onPeriodFilterChange={(v) => {
            setPeriodFilter(v)
            setCurrentPage(1)
          }}
          startDate={startDate}
          onStartDateChange={(d) => {
            setStartDate(d)
            setCurrentPage(1)
          }}
          endDate={endDate}
          onEndDateChange={(d) => {
            setEndDate(d)
            setCurrentPage(1)
          }}
        />
        <div
          style={
            { "--max-width": filteredEventos.length <= 2 ? "400px" : "1fr" } as React.CSSProperties
          }
          className="grid grid-cols-[repeat(auto-fit,minmax(calc(5vw+250px),var(--max-width)))] gap-4"
        >
          {filteredEventos.map((evento) => (
            <motion.div
              key={evento.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0, transition: { delay: 0.5 } }}
              viewport={{ once: true }}
            >
              <EventCardV2
                evento={evento}
                onSubscribedCounterClick={(e) => {
                  e.stopPropagation()
                  navigate(`/events/${evento.id}`, { state: { evento } })
                }}
                onCardClick={() => navigate(`/events/${evento.id}`, { state: { evento } })}
                onAvatarClcik={(id) => {
                  setMemebrId(id)
                  setIsMemberModalOpened(true)
                }}
                onSubscribeEvent={() => onSubscribeEvent(evento)}
              />
            </motion.div>
          ))}
        </div>

        {/* PAGINAÇÃO */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pb-6 pt-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex size-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="fi fi-ts-arrow-left flex items-center justify-center text-sm" />
            </button>

            {renderPageNumbers()}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex size-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="fi fi-ts-arrow-right flex items-center justify-center text-sm" />
            </button>
          </div>
        )}
      </div>

      {/* MODAL DE PDF */}
      {pdfConfig && (
        <PDFModal
          url={pdfConfig.url}
          title={pdfConfig.title}
          onClose={() => setPdfConfig(null)}
        />
      )}

      {selectedEventToBatch && (
        <BatchSubscribeModal
          isOpen={batchSubscribeModal}
          onBatchSubscribe={() => {
            showLoading()
            fetchEventos(currentPage)
          }}
          event={selectedEventToBatch}
          onClose={() => setBatchSubscribeModal(false)}
        />
      )}
      <MemberModal
        isOpen={isMemberModalOpened}
        onClose={() => setIsMemberModalOpened(false)}
        memberId={memebrId}
      />

      <CustomModal
        isOpen={modalState.isOpen}
        title={modalState.title}
        description={modalState.description}
        onClose={() => setModalState({ isOpen: false, title: "", description: "" })}
      />

    </div>
  )
}
